'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useLanguage } from '@/lib/LanguageContext';
import { getTranslations } from '@/lib/i18n';

// metadata removed - client component

const capabilities = [
  {
    icon: 'fas fa-search',
    titleEs: 'Diagnóstico Digital',
    titleFr: 'Diagnostic Numérique',
    descriptionEs: 'Evaluamos el estado actual de tu organización para identificar brechas tecnológicas, ineficiencias operativas y oportunidades de mejora con metodologías probadas.',
    descriptionFr: 'Nous évaluons l\'état actuel de votre organisation pour identifier les lacunes technologiques, les inefficacités opérationnelles et les opportunités d\'amélioration avec des méthodologies éprouvées.',
  },
  {
    icon: 'fas fa-route',
    titleEs: 'Hoja de Ruta Tecnológica',
    titleFr: 'Feuille de Route Technologique',
    descriptionEs: 'Diseñamos un plan estratégico personalizado con etapas claras, prioridades definidas y métricas de éxito para guiar tu transformación digital paso a paso.',
    descriptionFr: 'Nous concevons un plan stratégique personnalisé avec des étapes claires, des priorités définies et des métriques de succès pour guider votre transformation numérique étape par étape.',
  },
  {
    icon: 'fas fa-cogs',
    titleEs: 'Implementación Guiada',
    titleFr: 'Implémentation Guidée',
    descriptionEs: 'Acompañamos cada fase de implementación con expertos dedicados, asegurando que cada solución se integre correctamente a tus procesos existentes.',
    descriptionFr: 'Nous accompagnons chaque phase d\'implémentation avec des experts dédiés, en veillant à ce que chaque solution s\'intègre correctement à vos processus existants.',
  },
  {
    icon: 'fas fa-chart-line',
    titleEs: 'Gestión del Cambio',
    titleFr: 'Gestion du Changement',
    descriptionEs: 'Facilitamos la adopción tecnológica dentro de tu equipo con estrategias de comunicación, capacitación y seguimiento continuo.',
    descriptionFr: 'Nous facilitons l\'adoption technologique au sein de votre équipe avec des stratégies de communication, de formation et de suivi continu.',
  },
  {
    icon: 'fas fa-shield-alt',
    titleEs: 'Cumplimiento Normativo',
    titleFr: 'Conformité Réglementaire',
    descriptionEs: 'Asesoramos en el cumplimiento de regulaciones sectoriales, marcos de gobernanza TI y estándares internacionales aplicables a tu industria.',
    descriptionFr: 'Nous conseillons sur la conformité aux réglementations sectorielles, aux cadres de gouvernance IT et aux normes internationales applicables à votre secteur.',
  },
  {
    icon: 'fas fa-handshake',
    titleEs: 'Alianza Estratégica',
    titleFr: 'Alliance Stratégique',
    descriptionEs: 'Nos convertimos en tu socio tecnológico de largo plazo, con revisiones periódicas y ajustes continuos para mantener la competitividad de tu organización.',
    descriptionFr: 'Nous devenons votre partenaire technologique à long terme, avec des révisions périodiques et des ajustements continus pour maintenir la compétitivité de votre organisation.',
  },
];

const sectors = [
  { icon: 'fas fa-hospital', labelEs: 'Salud', labelFr: 'Santé' },
  { icon: 'fas fa-university', labelEs: 'Educación', labelFr: 'Éducation' },
  { icon: 'fas fa-building', labelEs: 'Gobierno', labelFr: 'Gouvernement' },
  { icon: 'fas fa-industry', labelEs: 'Industria', labelFr: 'Industrie' },
  { icon: 'fas fa-store', labelEs: 'Retail', labelFr: 'Commerce' },
  { icon: 'fas fa-leaf', labelEs: 'Sustentabilidad', labelFr: 'Durabilité' },
];

export default function AsesoriaConsultoriaPage() {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const s = t?.services;

  const isFr = language === 'fr';

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Nunito Sans', 'Raleway', arial, sans-serif" }}>
      <Navbar />
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ paddingTop: '160px', paddingBottom: '80px', backgroundColor: '#0a0a0a' }}>
        <div className="absolute top-20 right-10 opacity-20 hidden lg:block">
          <img src="https://innobox.cl/wp-content/uploads/2021/07/Dots-4.svg" alt="" className="w-40 h-40" />
        </div>
        <div className="absolute bottom-0 left-0 opacity-10 hidden lg:block">
          <img src="https://innobox.cl/wp-content/uploads/2021/07/Abstract-Circle.svg" alt="" className="w-64 h-64" />
        </div>

        <div className="max-w-[1200px] mx-auto px-[35px] relative z-10">
          <nav className="mb-8 flex items-center gap-2 text-sm" style={{ color: '#888' }}>
            <Link href="/" className="hover:text-[#1AEF0F] transition-colors">{s?.breadcrumbHome}</Link>
            <span>/</span>
            <Link href="/#servicios" className="hover:text-[#1AEF0F] transition-colors">{s?.breadcrumbServices}</Link>
            <span>/</span>
            <span style={{ color: '#1AEF0F' }}>{isFr ? 'Conseil et Consulting' : 'Asesoría y Consultoría'}</span>
          </nav>

          <div className="flex flex-col lg:flex-row items-start gap-12">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 mb-6 px-4 py-2" style={{ border: '1px solid #1AEF0F', color: '#1AEF0F', fontSize: '13px', letterSpacing: '2px' }}>
                <i className="fas fa-drafting-compass" />
                <span>{isFr ? 'SERVICE STRATÉGIQUE' : 'SERVICIO ESTRATÉGICO'}</span>
              </div>
              <h1 className="text-white font-bold mb-6" style={{ fontSize: 'clamp(32px, 4vw, 56px)', fontFamily: "'Titillium Web', sans-serif", lineHeight: '1.15' }}>
                {isFr ? 'Conseil et' : 'Asesoría y'}<br />
                <span style={{ color: '#1AEF0F' }}>{isFr ? 'Consulting' : 'Consultoría'}</span>
              </h1>
              <p className="mb-8" style={{ fontSize: '18px', color: '#aaa', lineHeight: '1.7', maxWidth: '560px' }}>
                {isFr
                  ? 'Planification numérique efficace pour les organisations qui cherchent à croître avec la technologie. Nous transformons des défis complexes en stratégies claires et exécutables.'
                  : 'Planificación digital efectiva para organizaciones que buscan crecer con tecnología. Transformamos desafíos complejos en estrategias claras y ejecutables.'}
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="https://calendar.app.google/qrMMuz1WHJJ4eoab8" target="_blank" rel="noopener noreferrer"
                  className="group relative inline-flex items-center overflow-hidden"
                  style={{ border: '1px solid #1AEF0F', padding: '14px 32px', fontSize: '15px', fontWeight: 'bold', color: '#1AEF0F', backgroundColor: 'transparent', transition: 'all 300ms ease-out' }}>
                  <span className="relative z-10 transition-colors duration-300 group-hover:text-black">
                    {isFr ? 'Planifier une Consultation' : 'Agendar Consultoría'}
                  </span>
                  <span className="absolute inset-0 transform translate-x-full group-hover:translate-x-0 transition-transform duration-300" style={{ backgroundColor: '#1AEF0F', zIndex: 0 }} />
                </a>
                <Link href="/#servicios" className="inline-flex items-center gap-2 text-white hover:text-[#1AEF0F] transition-colors" style={{ fontSize: '15px', padding: '14px 0' }}>
                  <i className="fas fa-arrow-left text-sm" />
                  {isFr ? 'Voir tous les services' : 'Ver todos los servicios'}
                </Link>
              </div>
            </div>

            <div className="lg:w-72 grid grid-cols-2 gap-4">
              {[
                { value: '+10', labelEs: 'Años de experiencia', labelFr: 'Années d\'expérience' },
                { value: '+50', labelEs: 'Proyectos ejecutados', labelFr: 'Projets réalisés' },
                { value: '100%', labelEs: 'Enfoque personalizado', labelFr: 'Approche personnalisée' },
                { value: '6', labelEs: 'Sectores atendidos', labelFr: 'Secteurs servis' },
              ]?.map((stat, i) => (
                <div key={i} className="p-5" style={{ border: '1px solid #222', backgroundColor: '#111' }}>
                  <div className="font-bold mb-1" style={{ fontSize: '28px', color: '#1AEF0F', fontFamily: "'Titillium Web', sans-serif" }}>{stat?.value}</div>
                  <div style={{ fontSize: '12px', color: '#888', lineHeight: '1.4' }}>{isFr ? stat?.labelFr : stat?.labelEs}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* Capabilities */}
      <section style={{ paddingTop: '80px', paddingBottom: '80px', backgroundColor: '#fff' }}>
        <div className="max-w-[1200px] mx-auto px-[35px]">
          <div className="mb-12">
            <h2 className="font-bold text-black mb-3" style={{ fontSize: 'clamp(24px, 2.5vw, 36px)', fontFamily: "'Titillium Web', sans-serif" }}>
              {s?.capabilitiesTitle}
            </h2>
            <p style={{ fontSize: '16px', color: '#505050', maxWidth: '600px', lineHeight: '1.6' }}>
              {isFr
                ? 'Un processus structuré qui transforme la complexité technologique en avantages concurrentiels réels pour votre organisation.'
                : 'Un proceso estructurado que convierte la complejidad tecnológica en ventajas competitivas reales para tu organización.'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0">
            {capabilities?.map((cap, i) => (
              <div key={i} className="group p-8 transition-all duration-300 hover:bg-[#f9f9f9]" style={{ borderBottom: '1px solid #E2E2E2', borderRight: i % 3 !== 2 ? '1px solid #E2E2E2' : 'none' }}>
                <div className="mb-5 flex items-center justify-center" style={{ width: '52px', height: '52px', border: '1px solid #E2E2E2', borderRadius: '50%' }}>
                  <i className={`${cap?.icon} text-xl text-black group-hover:text-[#1AEF0F] transition-colors duration-300`} />
                </div>
                <h3 className="font-semibold text-black mb-3" style={{ fontSize: '17px', fontFamily: "'Titillium Web', sans-serif" }}>
                  {isFr ? cap?.titleFr : cap?.titleEs}
                </h3>
                <p style={{ fontSize: '14px', color: '#505050', lineHeight: '1.6' }}>{isFr ? cap?.descriptionFr : cap?.descriptionEs}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Sectors */}
      <section style={{ paddingTop: '60px', paddingBottom: '80px', backgroundColor: '#f5f5f5' }}>
        <div className="max-w-[1200px] mx-auto px-[35px]">
          <h2 className="font-bold text-black mb-10" style={{ fontSize: 'clamp(22px, 2vw, 30px)', fontFamily: "'Titillium Web', sans-serif" }}>
            {s?.sectorsTitle}
          </h2>
          <div className="flex flex-wrap gap-4">
            {sectors?.map((sec, i) => (
              <div key={i} className="flex items-center gap-3 px-6 py-4 bg-white" style={{ border: '1px solid #E2E2E2' }}>
                <i className={`${sec?.icon} text-[#1AEF0F]`} />
                <span style={{ fontSize: '15px', fontWeight: '600', color: '#111' }}>{isFr ? sec?.labelFr : sec?.labelEs}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA */}
      <section style={{ paddingTop: '70px', paddingBottom: '70px', backgroundColor: '#1AEF0F' }}>
        <div className="max-w-[1200px] mx-auto px-[35px] flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="text-black font-bold m-0" style={{ fontSize: 'clamp(18px, 2vw, 26px)', fontFamily: "'Titillium Web', sans-serif", lineHeight: '1.3' }}>
            {isFr ? 'Prêt à démarrer votre transformation numérique ?' : '¿Listo para iniciar tu transformación digital?'}
          </p>
          <Link href="/contact" className="group relative inline-flex items-center overflow-hidden border border-black text-black bg-transparent flex-shrink-0" style={{ padding: '14px 32px', fontSize: '15px', fontWeight: 'bold', transition: 'all 300ms ease-out' }}>
            <span className="relative z-10 transition-colors duration-300 group-hover:text-white">{s?.ctaBtnContact}</span>
            <span className="absolute inset-0 bg-black transform translate-x-full group-hover:translate-x-0 transition-transform duration-300" style={{ zIndex: 0 }} />
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
